import { useEffect, useState } from 'react';
import {
  type AdminFilterState,
  type AdminPartnerRow,
  type AdminCampaignOption,
  type DatePreset,
  type PlatformFilter,
  type InstallMethod,
  type CreditStatusFilter,
  type PartnerStatusFilter,
  type PayoutStatusFilter,
  type AdminTableSort,
  applyDatePreset,
  INSTALL_METHOD_LABELS,
} from '../../lib/partnerStatsFilters';
import { countryCodeToFlag, countryCodeToName } from '../../lib/countryUtils';
import styles from './AdminFilters.module.css';

const PLATFORM_OPTIONS: { value: PlatformFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
  { value: 'desktop', label: 'Desktop' },
];

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'all_time', label: 'All time' },
];

const METHOD_OPTIONS: { value: 'all' | InstallMethod; label: string }[] = [
  { value: 'all', label: 'All methods' },
  { value: 'deeplink', label: INSTALL_METHOD_LABELS.deeplink },
  { value: 'fingerprint', label: INSTALL_METHOD_LABELS.fingerprint },
  { value: 'manual_code', label: INSTALL_METHOD_LABELS.manual_code },
  { value: 'other', label: INSTALL_METHOD_LABELS.other },
];

const SORT_OPTIONS: { value: AdminTableSort; label: string }[] = [
  { value: 'installs', label: 'Installs' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'earnings', label: 'Earnings' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'conversion', label: 'Conversion' },
  { value: 'name', label: 'Name' },
];

interface AdminFiltersProps {
  filters: AdminFilterState;
  partners: AdminPartnerRow[];
  countries: string[];
  campaigns: AdminCampaignOption[];
  onChange: (next: AdminFilterState) => void;
  onExportCsv: () => void;
}

export function AdminFilters({
  filters,
  partners,
  countries,
  campaigns,
  onChange,
  onExportCsv,
}: AdminFiltersProps) {
  const [draftDateFrom, setDraftDateFrom] = useState(filters.dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(filters.dateTo);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    setDraftDateFrom(filters.dateFrom);
    setDraftDateTo(filters.dateTo);
  }, [filters.dateFrom, filters.dateTo]);

  function patch(partial: Partial<AdminFilterState>) {
    onChange({ ...filters, ...partial });
  }

  function applyDates() {
    patch({ dateFrom: draftDateFrom, dateTo: draftDateTo });
  }

  function clearDates() {
    setDraftDateFrom('');
    setDraftDateTo('');
    patch({ dateFrom: '', dateTo: '' });
  }

  function applyPreset(preset: DatePreset) {
    const { dateFrom, dateTo } = applyDatePreset(preset);
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
    patch({ dateFrom, dateTo });
  }

  const hasAppliedDateFilter = Boolean(filters.dateFrom || filters.dateTo);
  const hasPendingDateChanges =
    draftDateFrom !== filters.dateFrom || draftDateTo !== filters.dateTo;

  return (
    <div className={styles.filtersCard}>
      <div className={styles.filtersHeader}>
        <h2 className={styles.filtersTitle}>Filters</h2>
        <div className={styles.headerActions}>
          <button type="button" className={styles.exportBtn} onClick={onExportCsv}>
            Export CSV
          </button>
          {hasAppliedDateFilter ? (
            <button type="button" className={styles.clearBtn} onClick={clearDates}>
              Clear dates
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.compactRow}>
        <div className={styles.inlineGroup}>
          <span className={styles.filterLabel}>Range</span>
          <div className={styles.pillGroup} role="group" aria-label="Date presets">
            {DATE_PRESETS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={styles.pill}
                onClick={() => applyPreset(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.inlineGroup}>
          <span className={styles.filterLabel}>Platform</span>
          <div className={styles.pillGroup} role="group" aria-label="Platform filter">
            {PLATFORM_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={filters.platform === opt.value ? styles.pillActive : styles.pill}
                onClick={() => patch({ platform: opt.value })}
                aria-pressed={filters.platform === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.toolbarRow}>
        <label className={styles.selectField}>
          <span className={styles.filterLabel}>Partner</span>
          <select
            className={styles.select}
            value={filters.partnerId}
            onChange={(e) => patch({ partnerId: e.target.value })}
          >
            <option value="all">All partners</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName} ({p.referralCode})
              </option>
            ))}
          </select>
        </label>

        <label className={styles.selectField}>
          <span className={styles.filterLabel}>Status</span>
          <select
            className={styles.select}
            value={filters.partnerStatus}
            onChange={(e) =>
              patch({ partnerStatus: e.target.value as PartnerStatusFilter })
            }
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>

        <label className={styles.dateField}>
          <span className={styles.filterLabel}>From</span>
          <input
            type="date"
            className={styles.dateInput}
            value={draftDateFrom}
            onChange={(e) => setDraftDateFrom(e.target.value)}
            max={draftDateTo || undefined}
          />
        </label>
        <label className={styles.dateField}>
          <span className={styles.filterLabel}>To</span>
          <input
            type="date"
            className={styles.dateInput}
            value={draftDateTo}
            onChange={(e) => setDraftDateTo(e.target.value)}
            min={draftDateFrom || undefined}
          />
        </label>
        <div className={styles.applyWrap}>
          <button
            type="button"
            className={styles.applyBtn}
            onClick={applyDates}
            disabled={!hasPendingDateChanges}
          >
            Apply
          </button>
        </div>
      </div>

      <button
        type="button"
        className={styles.moreToggle}
        onClick={() => setMoreOpen((v) => !v)}
        aria-expanded={moreOpen}
      >
        {moreOpen ? 'Hide' : 'More'} filters
      </button>

      {moreOpen ? (
        <div className={styles.moreSection}>
          <div className={styles.selectRow}>
            <label className={styles.selectField}>
              <span className={styles.filterLabel}>Country</span>
              <select
                className={styles.select}
                value={filters.country}
                onChange={(e) => patch({ country: e.target.value })}
              >
                <option value="all">All countries</option>
                {countries.map((code) => (
                  <option key={code} value={code}>
                    {countryCodeToFlag(code)} {countryCodeToName(code)}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.selectField}>
              <span className={styles.filterLabel}>Install method</span>
              <select
                className={styles.select}
                value={filters.installMethod}
                onChange={(e) =>
                  patch({
                    installMethod: e.target.value as 'all' | InstallMethod,
                  })
                }
              >
                {METHOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.selectField}>
              <span className={styles.filterLabel}>Credit status</span>
              <select
                className={styles.select}
                value={filters.creditStatus}
                onChange={(e) =>
                  patch({ creditStatus: e.target.value as CreditStatusFilter })
                }
              >
                <option value="all">All</option>
                <option value="credited">Credited only</option>
                <option value="rejected">Rejected only</option>
              </select>
            </label>
          </div>

          <div className={styles.selectRow}>
            <label className={styles.selectField}>
              <span className={styles.filterLabel}>Campaign</span>
              <select
                className={styles.select}
                value={filters.campaignId}
                onChange={(e) => patch({ campaignId: e.target.value })}
              >
                <option value="all">All campaigns</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.shortCode})
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.selectField}>
              <span className={styles.filterLabel}>Payout status</span>
              <select
                className={styles.select}
                value={filters.payoutStatus}
                onChange={(e) =>
                  patch({ payoutStatus: e.target.value as PayoutStatusFilter })
                }
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </label>

            <label className={styles.selectField}>
              <span className={styles.filterLabel}>Min unpaid ($)</span>
              <input
                type="number"
                className={styles.select}
                min={0}
                step={0.01}
                value={filters.minUnpaid || ''}
                placeholder="0"
                onChange={(e) =>
                  patch({ minUnpaid: parseFloat(e.target.value) || 0 })
                }
              />
            </label>
          </div>
        </div>
      ) : null}

      <div className={styles.tableControls}>
        <label className={styles.searchField}>
          <span className={styles.filterLabel}>Search partners</span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Name or referral code..."
            value={filters.tableSearch}
            onChange={(e) => patch({ tableSearch: e.target.value })}
          />
        </label>

        <label className={styles.selectField}>
          <span className={styles.filterLabel}>Sort by</span>
          <select
            className={styles.select}
            value={filters.tableSort}
            onChange={(e) => patch({ tableSort: e.target.value as AdminTableSort })}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.selectField}>
          <span className={styles.filterLabel}>Order</span>
          <select
            className={styles.select}
            value={filters.tableSortDir}
            onChange={(e) =>
              patch({ tableSortDir: e.target.value as 'asc' | 'desc' })
            }
          >
            <option value="desc">High to low</option>
            <option value="asc">Low to high</option>
          </select>
        </label>
      </div>
    </div>
  );
}
